import React from 'react';
import { Slider } from '../ui/slider';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';

export const ParameterControl = ({ 
    label, 
    value, 
    onChange, 
    min, 
    max, 
    step = 1, 
    unit = '',
    color = '#D0F224'
}) => {
    const displayValue = typeof value === 'number' ? 
        (Number.isInteger(step) ? Math.round(value) : value.toFixed(1)) : value;

    return (
        <div className="param-control">
            <div className="flex items-center justify-between mb-1">
                <span className="param-label">{label}</span>
                <span className="param-value" style={{ color }}>{displayValue}{unit}</span>
            </div>
            <Slider
                value={[value]}
                onValueChange={([v]) => onChange(v)}
                min={min}
                max={max}
                step={step}
                className="w-full"
            />
        </div>
    );
};

export const ModuleCard = ({ title, enabled, onToggle, children, color = '#D0F224' }) => {
    return (
        <div className={`module-card p-4 ${enabled ? 'active' : ''}`}>
            <div className="control-section-header">
                <h4 className="font-heading text-sm font-bold uppercase tracking-wider" style={{ color }}>
                    {title}
                </h4>
                {onToggle && (
                    <Switch
                        checked={enabled}
                        onCheckedChange={onToggle}
                        data-testid={`${title.toLowerCase().replace(/\s/g, '-')}-toggle`}
                    />
                )}
            </div>
            <div className={`space-y-4 ${!enabled && onToggle ? 'opacity-50 pointer-events-none' : ''}`}>
                {children}
            </div>
        </div>
    );
};

export const EQModule = ({ settings, onUpdate }) => {
    return (
        <ModuleCard title="Equalizer" color="#D0F224">
            <ParameterControl
                label="Low Gain"
                value={settings.eq_low_gain}
                onChange={(v) => onUpdate('eq_low_gain', v)}
                min={-12}
                max={12}
                step={0.5}
                unit=" dB"
            />
            <ParameterControl
                label="Low Freq"
                value={settings.eq_low_freq}
                onChange={(v) => onUpdate('eq_low_freq', v)}
                min={20}
                max={500}
                step={10}
                unit=" Hz"
            />
            <ParameterControl
                label="Mid Gain"
                value={settings.eq_mid_gain}
                onChange={(v) => onUpdate('eq_mid_gain', v)}
                min={-12}
                max={12}
                step={0.5}
                unit=" dB"
            />
            <ParameterControl
                label="Mid Freq"
                value={settings.eq_mid_freq}
                onChange={(v) => onUpdate('eq_mid_freq', v)}
                min={200}
                max={5000}
                step={100}
                unit=" Hz"
            />
            <ParameterControl
                label="High Gain"
                value={settings.eq_high_gain}
                onChange={(v) => onUpdate('eq_high_gain', v)}
                min={-12}
                max={12}
                step={0.5}
                unit=" dB"
            />
            <ParameterControl
                label="High Freq"
                value={settings.eq_high_freq}
                onChange={(v) => onUpdate('eq_high_freq', v)}
                min={2000}
                max={16000}
                step={500}
                unit=" Hz"
            />
        </ModuleCard>
    );
};

export const CompressorModule = ({ settings, onUpdate }) => {
    return (
        <ModuleCard title="Compressor" color="#FF9F1C">
            <ParameterControl
                label="Threshold"
                value={settings.compressor_threshold}
                onChange={(v) => onUpdate('compressor_threshold', v)}
                min={-60}
                max={0}
                step={1}
                unit=" dB"
                color="#FF9F1C"
            />
            <ParameterControl
                label="Ratio"
                value={settings.compressor_ratio}
                onChange={(v) => onUpdate('compressor_ratio', v)}
                min={1}
                max={20}
                step={0.5}
                unit=":1"
                color="#FF9F1C"
            />
            <ParameterControl
                label="Attack"
                value={settings.compressor_attack}
                onChange={(v) => onUpdate('compressor_attack', v)}
                min={0.1}
                max={100}
                step={0.1}
                unit=" ms"
                color="#FF9F1C"
            />
            <ParameterControl
                label="Release"
                value={settings.compressor_release}
                onChange={(v) => onUpdate('compressor_release', v)}
                min={10}
                max={1000}
                step={10}
                unit=" ms"
                color="#FF9F1C"
            />
            <ParameterControl
                label="Makeup"
                value={settings.compressor_makeup}
                onChange={(v) => onUpdate('compressor_makeup', v)}
                min={0}
                max={24}
                step={0.5}
                unit=" dB"
                color="#FF9F1C"
            />
        </ModuleCard>
    );
};

export const LimiterModule = ({ settings, onUpdate }) => {
    return (
        <ModuleCard title="Limiter" color="#FF3B30">
            <ParameterControl
                label="Threshold"
                value={settings.limiter_threshold}
                onChange={(v) => onUpdate('limiter_threshold', v)}
                min={-12}
                max={0}
                step={0.1}
                unit=" dB"
                color="#FF3B30"
            />
            <ParameterControl
                label="Ceiling"
                value={settings.limiter_ceiling}
                onChange={(v) => onUpdate('limiter_ceiling', v)}
                min={-3}
                max={0}
                step={0.1}
                unit=" dB"
                color="#FF3B30"
            />
            <ParameterControl
                label="Release"
                value={settings.limiter_release}
                onChange={(v) => onUpdate('limiter_release', v)}
                min={10}
                max={500}
                step={5}
                unit=" ms"
                color="#FF3B30"
            />
        </ModuleCard>
    );
};

export const StereoModule = ({ settings, onUpdate }) => {
    return (
        <ModuleCard title="Stereo Imaging" color="#00F0FF">
            <ParameterControl
                label="Width"
                value={settings.stereo_width}
                onChange={(v) => onUpdate('stereo_width', v)}
                min={0}
                max={200}
                step={1}
                unit="%"
                color="#00F0FF"
            />
            <ParameterControl
                label="Mid/Side"
                value={settings.stereo_mid_side}
                onChange={(v) => onUpdate('stereo_mid_side', v)}
                min={-100}
                max={100}
                step={1}
                unit=""
                color="#00F0FF"
            />
        </ModuleCard>
    );
};

export const ExciterModule = ({ settings, onUpdate }) => {
    return (
        <ModuleCard 
            title="Harmonic Exciter" 
            enabled={settings.exciter_enabled}
            onToggle={(v) => onUpdate('exciter_enabled', v)}
            color="#7B2CBF"
        >
            <ParameterControl
                label="Drive"
                value={settings.exciter_drive}
                onChange={(v) => onUpdate('exciter_drive', v)}
                min={0}
                max={100}
                step={1}
                unit="%"
                color="#7B2CBF"
            />
            <ParameterControl
                label="Frequency"
                value={settings.exciter_frequency}
                onChange={(v) => onUpdate('exciter_frequency', v)}
                min={1000}
                max={10000}
                step={100}
                unit=" Hz"
                color="#7B2CBF"
            />
        </ModuleCard>
    );
};

export const DeesserModule = ({ settings, onUpdate }) => {
    return (
        <ModuleCard 
            title="De-esser" 
            enabled={settings.deesser_enabled}
            onToggle={(v) => onUpdate('deesser_enabled', v)}
            color="#FFD60A"
        >
            <ParameterControl
                label="Threshold"
                value={settings.deesser_threshold}
                onChange={(v) => onUpdate('deesser_threshold', v)}
                min={-60}
                max={0}
                step={1}
                unit=" dB"
                color="#FFD60A"
            />
            <ParameterControl
                label="Frequency"
                value={settings.deesser_frequency}
                onChange={(v) => onUpdate('deesser_frequency', v)}
                min={2000}
                max={12000}
                step={100}
                unit=" Hz"
                color="#FFD60A"
            />
            <ParameterControl
                label="Ratio"
                value={settings.deesser_ratio}
                onChange={(v) => onUpdate('deesser_ratio', v)}
                min={1}
                max={10}
                step={0.5}
                unit=":1"
                color="#FFD60A"
            />
        </ModuleCard>
    );
};

export const OutputModule = ({ settings, onUpdate }) => {
    return (
        <ModuleCard title="Output" color="#00FF94">
            <ParameterControl
                label="Gain"
                value={settings.output_gain}
                onChange={(v) => onUpdate('output_gain', v)}
                min={-12}
                max={12}
                step={0.5}
                unit=" dB"
                color="#00FF94"
            />
            <ParameterControl
                label="Target LUFS"
                value={settings.normalize_lufs}
                onChange={(v) => onUpdate('normalize_lufs', v)}
                min={-23}
                max={-9}
                step={0.5}
                unit=" LUFS"
                color="#00FF94"
            />
        </ModuleCard>
    );
};
